import React, { useEffect, useState, memo, createElement, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, Lock, Heart, Sparkles, Flame, MessageCircle, ChevronDown, ShieldCheck, Zap, EyeOff, Smartphone, Users, PartyPopper, Gift, CalendarHeart, Infinity as InfinityIcon, Unlock, RefreshCw, CreditCard, Layers, Dices } from 'lucide-react';
// --- Constants & Assets ---
const CHECKOUT_URL = 'https://origenlabonline.shop/cart/46317834797228:1';
const VIDEO_URL = 'https://cdn.magicpatterns.com/uploads/cEu1TvHBpr39hS8SPh2etf/oksy04210307_pindown.io_1780105522.mp4';
const APP_MOCKUP_URL = 'https://cdn.magicpatterns.com/uploads/whmdnU8pKp2GtsQuGBsNM3/ChatGPT_Image_28_may_2026,_11_26_36_a.m..png';
const LOGO_URL = 'https://cdn.magicpatterns.com/uploads/gsjCZQ89Mqk9Uz8MVVQDwE/WhatsApp_Image_2026-05-29_at_20.27.51.jpg';
const MP_LOGO_URL = 'https://cdn.magicpatterns.com/uploads/h9zBi5cHNRMB7BVWkHXyKr/descarga_(2).png';
// --- Reusable Components ---
const FadeIn = ({
  children,
  delay = 0,
  className = ''
}: {
  children: React.ReactNode;
  delay?: number;
  className?: string;
}) => <motion.div initial={{
  opacity: 0,
  y: 20
}} whileInView={{
  opacity: 1,
  y: 0
}} viewport={{
  once: true,
  margin: '-50px'
}} transition={{
  duration: 0.6,
  delay,
  ease: 'easeOut'
}} className={className} data-id="element-0">
    {children}
  </motion.div>;
const CTAButton = ({
  children,
  className = ''
}: {
  children: React.ReactNode;
  className?: string;
}) => <a href={CHECKOUT_URL} className={`group relative inline-flex items-center justify-center px-8 py-4 text-sm md:text-base font-bold text-white uppercase tracking-widest bg-[#FF2D7A] rounded-full transition-all hover:scale-105 shadow-[0_0_20px_rgba(255,45,122,0.4)] hover:shadow-[0_0_30px_rgba(255,45,122,0.6)] w-full sm:w-auto ${className}`} data-id="element-1">
    <span className="relative flex items-center gap-2" data-id="element-2">{children}</span>
  </a>;
// --- Sections ---
const HeroSection = () => <section className="relative min-h-[90svh] flex items-center justify-center overflow-hidden pt-20 pb-10" data-id="element-3">
    {/* Background Video - Increased visibility */}
    <div className="absolute inset-0 w-full h-full" data-id="element-4">
      <video autoPlay loop muted playsInline className="absolute inset-0 w-full h-full object-cover opacity-75" data-id="element-5">
        <source src={VIDEO_URL} type="video/mp4" data-id="element-6" />
      </video>
      {/* Lighter gradients to make the couple pop more */}
      <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/20 to-black/95" data-id="element-7" />
    </div>

    {/* Header/Logo */}
    <div className="absolute top-0 left-0 w-full p-4 flex justify-center z-20" data-id="element-8">
      <img src={LOGO_URL} alt="El Juego del Deseo" className="h-10 md:h-14 rounded-full opacity-90" data-id="element-9" />
    </div>

    {/* Content */}
    <div className="relative z-10 container mx-auto px-4 flex flex-col items-center text-center mt-8" data-id="element-10">
      <FadeIn data-id="element-11">
        <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold tracking-tight mb-4 text-white text-glow" data-id="element-12">
          ESTA NOCHE PUEDE
          <br data-id="element-13" />
          <span className="text-[#FF2D7A]" data-id="element-14">SER DISTINTA.</span>
        </h1>
      </FadeIn>

      <FadeIn delay={0.1} data-id="element-15">
        <p className="text-lg md:text-xl text-zinc-200 max-w-2xl mx-auto mb-8 font-light leading-relaxed drop-shadow-md" data-id="element-16">
          Preguntas, retos y experiencias diseñadas para parejas que quieren
          salir de la rutina y volver a sorprenderse.
        </p>
      </FadeIn>

      <FadeIn delay={0.2} className="w-full max-w-md mx-auto mb-8" data-id="element-17">
        <ul className="flex flex-col gap-3" data-id="element-18">
          {[{
          text: 'Más conexión',
          icon: Heart
        }, {
          text: 'Más diversión',
          icon: Sparkles
        }, {
          text: 'Más complicidad',
          icon: Flame
        }, {
          text: '100% privado',
          icon: Lock
        }].map((item, i) => <li key={i} className="flex items-center gap-3 text-white bg-black/40 backdrop-blur-md py-3 px-5 rounded-xl border border-white/10" data-id="element-19">
              <item.icon className="w-5 h-5 text-[#FF2D7A]" data-id="element-20" />
              <span className="font-medium tracking-wide" data-id="element-21">{item.text}</span>
            </li>)}
        </ul>
      </FadeIn>

      <FadeIn delay={0.3} className="flex flex-col items-center w-full max-w-md" data-id="element-22">
        <CTAButton className="mb-4" data-id="element-23">DESBLOQUEAR MI EXPERIENCIA</CTAButton>

        <p className="text-sm text-zinc-300 mb-3 tracking-wide font-light drop-shadow-md" data-id="element-24">
          Acceso completo por{' '}
          <span className="text-white font-bold" data-id="element-25">$16.990</span> · Pago único
        </p>

        <div className="flex items-center gap-2 text-xs text-zinc-300 tracking-wider uppercase bg-black/50 px-4 py-2 rounded-full backdrop-blur-sm" data-id="element-26">
          <Lock className="w-3 h-3 text-[#FF2D7A]" data-id="element-27" />
          <span data-id="element-28">Pago seguro con Mercado Pago</span>
        </div>
      </FadeIn>
    </div>
  </section>;
const IncludesSection = () => {
  const items = [{
    title: 'Ruleta de la Pasión',
    icon: Flame
  }, {
    title: 'Cartas del Deseo',
    icon: Layers
  }, {
    title: 'Verdad o Atrevimiento',
    icon: MessageCircle
  }, {
    title: 'Desafíos para parejas',
    icon: Dices
  }, {
    title: 'Experiencias personalizadas',
    icon: Sparkles
  }, {
    title: 'Más de 1.000 combinaciones',
    icon: InfinityIcon
  }];
  return <section className="py-16 md:py-20 bg-black relative overflow-hidden" data-id="element-29">
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-[#FF2D7A]/5 rounded-full blur-[120px] pointer-events-none" data-id="element-30" />

      <div className="container mx-auto px-4" data-id="element-31">
        <div className="flex flex-col lg:flex-row items-center gap-10" data-id="element-32">
          <FadeIn className="w-full lg:w-1/2 flex justify-center order-2 lg:order-1" data-id="element-33">
            <img src={APP_MOCKUP_URL} alt="App Interface" className="max-w-full h-auto w-[280px] md:w-[350px] drop-shadow-[0_0_30px_rgba(255,45,122,0.15)]" data-id="element-34" />
          </FadeIn>

          <div className="w-full lg:w-1/2 order-1 lg:order-2" data-id="element-35">
            <FadeIn data-id="element-36">
              <h2 className="text-3xl md:text-4xl font-bold mb-8 leading-tight" data-id="element-37">
                ¿QUÉ INCLUYE <span className="text-[#FF2D7A]" data-id="element-38">EL JUEGO?</span>
              </h2>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8" data-id="element-39">
                {items.map((item, i) => <div key={i} className="flex items-center gap-3 bg-white/[0.03] border border-white/5 p-4 rounded-xl" data-id="element-40">
                    <item.icon className="w-6 h-6 text-[#FF2D7A] flex-shrink-0" data-id="element-41" />
                    <span className="text-zinc-200 font-light" data-id="element-42">
                      {item.title}
                    </span>
                  </div>)}
              </div>

              <CTAButton data-id="element-43">QUIERO ACCEDER</CTAButton>
            </FadeIn>
          </div>
        </div>
      </div>
    </section>;
};
const WhyItWorksSection = () => {
  const benefits = ['Rompe el hielo rápidamente', 'Genera conversaciones inesperadas', 'Crea momentos memorables', 'Se adapta al nivel de intensidad que elijan', 'Cada experiencia es diferente', 'Miles de combinaciones posibles', 'No se vuelve repetitivo'];
  return <section className="py-16 md:py-20 bg-[#030303] border-y border-white/5" data-id="element-44">
      <div className="container mx-auto px-4 max-w-4xl" data-id="element-45">
        <FadeIn className="text-center mb-10" data-id="element-46">
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight" data-id="element-47">
            ¿POR QUÉ <span className="text-[#FF2D7A]" data-id="element-48">FUNCIONA?</span>
          </h2>
        </FadeIn>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-10" data-id="element-49">
          {benefits.map((benefit, i) => <FadeIn key={i} delay={i * 0.05} className="flex items-start gap-3" data-id="element-50">
              <div className="mt-1 bg-[#FF2D7A]/10 p-1 rounded-full flex-shrink-0" data-id="element-51">
                <Check className="w-4 h-4 text-[#FF2D7A]" data-id="element-52" />
              </div>
              <span className="text-zinc-300 font-light text-lg" data-id="element-53">
                {benefit}
              </span>
            </FadeIn>)}
        </div>

        <FadeIn className="flex justify-center" data-id="element-54">
          <CTAButton data-id="element-55">EMPEZAR AHORA</CTAButton>
        </FadeIn>
      </div>
    </section>;
};
const WhoIsItForSection = () => {
  const audiences = [{
    text: 'Parejas nuevas que quieren conocerse mejor',
    icon: Heart
  }, {
    text: 'Parejas de años que quieren salir de la rutina',
    icon: RefreshCw
  }, {
    text: 'Citas románticas',
    icon: Flame
  }, {
    text: 'Aniversarios y ocasiones especiales',
    icon: CalendarHeart
  }, {
    text: 'Personas que quieren sorprender a su pareja',
    icon: Gift
  }, {
    text: 'Grupos de amigos que buscan diversión',
    icon: Users
  }, {
    text: 'Reuniones privadas y fiestas',
    icon: PartyPopper
  }, {
    text: 'Cualquiera que quiera una experiencia diferente',
    icon: Sparkles
  }];
  return <section className="py-16 md:py-20 bg-black" data-id="element-56">
      <div className="container mx-auto px-4" data-id="element-57">
        <FadeIn className="text-center mb-12" data-id="element-58">
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-4" data-id="element-59">
            JUGÁ COMO <span className="text-[#FF2D7A]" data-id="element-60">QUIERAS</span>
          </h2>
          <p className="text-xl text-zinc-400 font-light" data-id="element-61">
            No es solo para parejas. El Juego del Deseo incluye experiencias
            para todos.
          </p>
        </FadeIn>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 max-w-6xl mx-auto" data-id="element-62">
          {audiences.map((aud, i) => <FadeIn key={i} delay={i * 0.05} data-id="element-63">
              <div className="bg-white/[0.02] border border-white/5 p-6 rounded-2xl h-full flex flex-col items-center text-center hover:border-[#FF2D7A]/30 transition-colors" data-id="element-64">
                <aud.icon className="w-8 h-8 text-[#FF2D7A] mb-4 opacity-80" data-id="element-65" />
                <span className="text-zinc-300 font-light text-sm leading-relaxed" data-id="element-66">
                  {aud.text}
                </span>
              </div>
            </FadeIn>)}
        </div>
      </div>
    </section>;
};
const ValueStackSection = () => {
  const items = ['Acceso inmediato', 'Todas las modalidades desbloqueadas', 'Actualizaciones futuras', 'Compatible con cualquier celular', 'Sin descargas', 'Uso ilimitado', 'Experiencias para todos los niveles'];
  return <section className="py-16 md:py-20 bg-[#030303] border-t border-white/5" data-id="element-67">
      <div className="container mx-auto px-4 max-w-3xl" data-id="element-68">
        <FadeIn className="bg-gradient-to-b from-white/[0.05] to-transparent border border-white/10 rounded-3xl p-8 md:p-12 text-center relative overflow-hidden" data-id="element-69">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-1 bg-gradient-to-r from-transparent via-[#FF2D7A] to-transparent opacity-50" data-id="element-70" />

          <h2 className="text-2xl md:text-3xl font-bold mb-8" data-id="element-71">
            TODO LO QUE <span className="text-[#FF2D7A]" data-id="element-72">RECIBÍS</span>
          </h2>

          <div className="flex flex-col gap-4 mb-10 text-left max-w-md mx-auto" data-id="element-73">
            {items.map((item, i) => <div key={i} className="flex items-center gap-4 border-b border-white/5 pb-4 last:border-0 last:pb-0" data-id="element-74">
                <div className="bg-[#FF2D7A]/20 p-1.5 rounded-full" data-id="element-75">
                  <Check className="w-5 h-5 text-[#FF2D7A]" data-id="element-76" />
                </div>
                <span className="text-zinc-200 font-light text-lg" data-id="element-77">{item}</span>
              </div>)}
          </div>

          <CTAButton data-id="element-78">DESBLOQUEAR TODO AHORA</CTAButton>
        </FadeIn>
      </div>
    </section>;
};
const SocialProofSection = () => {
  const testimonials = [{
    id: 1,
    user: 'martina.g',
    text: 'Lo compramos para una noche tranquila y terminamos jugando hasta las 3 AM.',
    likes: 124,
    avatar: 'https://i.pravatar.cc/150?img=47'
  }, {
    id: 2,
    user: 'juanpablo_88',
    text: 'Pensé que era otro juego más, pero nos sorprendió muchísimo.',
    likes: 89,
    avatar: 'https://i.pravatar.cc/150?img=11'
  }, {
    id: 3,
    user: 'cami_y_leo',
    text: 'Nos hizo hablar de cosas que nunca habían salido.',
    likes: 256,
    avatar: 'https://i.pravatar.cc/150?img=32'
  }, {
    id: 4,
    user: 'sofi.mendez',
    text: 'La ruleta fue lo mejor.',
    likes: 45,
    avatar: 'https://i.pravatar.cc/150?img=5'
  }, {
    id: 5,
    user: 'nico_ferrari',
    text: 'Lo llevamos a una reunión con amigos y fue un éxito.',
    likes: 112,
    avatar: 'https://i.pravatar.cc/150?img=12'
  }, {
    id: 6,
    user: 'valen.ruiz',
    text: 'Cada ronda era distinta.',
    likes: 67,
    avatar: 'https://i.pravatar.cc/150?img=44'
  }];
  return <section className="py-16 bg-black border-t border-white/5 overflow-hidden" data-id="element-79">
      <div className="container mx-auto px-4 max-w-5xl" data-id="element-80">
        <FadeIn className="text-center mb-10" data-id="element-81">
          <h2 className="text-2xl md:text-3xl font-bold tracking-tight" data-id="element-82">
            LO QUE DICEN{' '}
            <span className="text-[#FF2D7A]" data-id="element-83">QUIENES YA JUGARON</span>
          </h2>
        </FadeIn>

        <div className="flex overflow-x-auto snap-x snap-mandatory gap-4 pb-8 -mx-4 px-4 md:mx-0 md:px-0 md:grid md:grid-cols-2 lg:grid-cols-3 md:overflow-visible md:snap-none hide-scrollbar" data-id="element-84">
          {testimonials.map((t, i) => <FadeIn key={t.id} delay={i * 0.05} className="min-w-[280px] sm:min-w-[320px] md:min-w-0 snap-center flex-shrink-0" data-id="element-85">
              <div className="bg-[#0a0a0a] border border-zinc-800/60 p-5 rounded-2xl flex gap-3 h-full" data-id="element-86">
                <img src={t.avatar} alt={t.user} className="w-10 h-10 rounded-full object-cover flex-shrink-0 border border-zinc-800" data-id="element-87" />
                <div className="flex-1" data-id="element-88">
                  <p className="text-sm font-semibold text-zinc-100" data-id="element-89">
                    {t.user}
                  </p>
                  <p className="text-sm text-zinc-300 mt-1 leading-snug font-light" data-id="element-90">
                    {t.text}
                  </p>
                  <div className="flex items-center gap-4 mt-3" data-id="element-91">
                    <span className="text-xs text-zinc-500 font-medium" data-id="element-92">
                      {t.likes} Me gusta
                    </span>
                    <span className="text-xs text-zinc-600 font-medium cursor-pointer" data-id="element-93">
                      Responder
                    </span>
                  </div>
                </div>
                <div className="flex-shrink-0 pt-1" data-id="element-94">
                  <Heart className="w-4 h-4 text-zinc-500 hover:text-[#FF2D7A] transition-colors cursor-pointer" data-id="element-95" />
                </div>
              </div>
            </FadeIn>)}
        </div>
      </div>
    </section>;
};
const FAQSection = () => {
  const faqs = [{
    q: '¿Funciona en celular?',
    a: 'Sí, está optimizado para dispositivos móviles.'
  }, {
    q: '¿Necesito descargar algo?',
    a: 'No. Accedés directamente desde el navegador.'
  }, {
    q: '¿Es una suscripción?',
    a: 'No. Es un pago único.'
  }, {
    q: '¿Recibo acceso inmediatamente?',
    a: 'Sí, automáticamente luego de la compra.'
  }];
  const [openIndex, setOpenIndex] = useState<number | null>(0);
  return <section className="py-16 bg-[#030303] border-t border-white/5" data-id="element-96">
      <div className="container mx-auto px-4 max-w-3xl" data-id="element-97">
        <FadeIn className="text-center mb-10" data-id="element-98">
          <h2 className="text-2xl md:text-3xl font-bold tracking-tight" data-id="element-99">
            PREGUNTAS FRECUENTES
          </h2>
        </FadeIn>

        <div className="space-y-2" data-id="element-100">
          {faqs.map((faq, i) => <FadeIn key={i} delay={i * 0.05} data-id="element-101">
              <div className={`border-b border-white/10 overflow-hidden transition-colors ${openIndex === i ? 'bg-white/[0.02]' : 'bg-transparent'}`} data-id="element-102">
                <button className="w-full px-4 py-5 text-left flex justify-between items-center focus:outline-none" onClick={() => setOpenIndex(openIndex === i ? null : i)} data-id="element-103">
                  <span className="font-light text-zinc-200" data-id="element-104">{faq.q}</span>
                  <ChevronDown className={`w-5 h-5 text-zinc-500 transition-transform duration-300 ${openIndex === i ? 'rotate-180 text-[#FF2D7A]' : ''}`} data-id="element-105" />
                </button>
                <AnimatePresence data-id="element-106">
                  {openIndex === i && <motion.div initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} transition={{
                duration: 0.3
              }} data-id="element-107">
                      <div className="px-4 pb-5 text-zinc-400 font-light text-sm" data-id="element-108">
                        {faq.a}
                      </div>
                    </motion.div>}
                </AnimatePresence>
              </div>
            </FadeIn>)}
        </div>
      </div>
    </section>;
};
const TrustAndPricingSection = () => <section className="py-20 relative overflow-hidden bg-black" data-id="element-109">
    <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(255,45,122,0.08)_0%,rgba(0,0,0,1)_70%)]" data-id="element-110" />
    <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-[#FF2D7A]/30 to-transparent" data-id="element-111" />

    <div className="container mx-auto px-4 relative z-10 text-center max-w-3xl" data-id="element-112">
      <FadeIn data-id="element-113">
        {/* Trust Badges */}
        <div className="flex flex-col items-center mb-10" data-id="element-114">
          <img src={MP_LOGO_URL} alt="Mercado Pago" className="h-12 object-contain mb-6 opacity-90" data-id="element-115" />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm text-zinc-300 font-light text-left bg-white/[0.03] p-6 rounded-2xl border border-white/10 w-full max-w-lg mx-auto" data-id="element-116">
            <div className="flex items-center gap-2" data-id="element-117">
              <ShieldCheck className="w-4 h-4 text-[#FF2D7A]" data-id="element-118" /> Pago seguro con
              Mercado Pago
            </div>
            <div className="flex items-center gap-2" data-id="element-119">
              <CreditCard className="w-4 h-4 text-[#FF2D7A]" data-id="element-120" /> Tarjetas de
              crédito y débito
            </div>
            <div className="flex items-center gap-2" data-id="element-121">
              <RefreshCw className="w-4 h-4 text-[#FF2D7A]" data-id="element-122" /> Posibilidad de
              pago en cuotas
            </div>
            <div className="flex items-center gap-2" data-id="element-123">
              <Zap className="w-4 h-4 text-[#FF2D7A]" data-id="element-124" /> Acceso inmediato al
              comprar
            </div>
            <div className="flex items-center gap-2 sm:col-span-2" data-id="element-125">
              <Unlock className="w-4 h-4 text-[#FF2D7A]" data-id="element-126" /> Sin suscripciones
              mensuales
            </div>
          </div>
        </div>

        <h2 className="text-3xl md:text-4xl font-bold mb-6 tracking-tight" data-id="element-127">
          DESCUBRÍ QUÉ EXPERIENCIA <br data-id="element-128" />
          <span className="text-[#FF2D7A]" data-id="element-129">ENCAJA CON USTEDES</span>
        </h2>

        <div className="mb-6" data-id="element-130">
          <span className="text-6xl md:text-7xl font-bold text-white text-glow tracking-tighter" data-id="element-131">
            $16.990
          </span>
        </div>

        <p className="text-xl text-[#FF2D7A] font-medium mb-6" data-id="element-132">
          Acceso completo por única vez
        </p>

        <div className="flex flex-col gap-4 text-zinc-300 text-left max-w-sm mx-auto mb-10" data-id="element-133">
          <div className="flex items-center gap-3" data-id="element-134">
            <span className="text-xl leading-none" data-id="element-135">💳</span>
            <span className="font-light" data-id="element-136">
              Hasta 3 cuotas con Mercado Pago sin interes
            </span>
          </div>
          <div className="flex items-center gap-3" data-id="element-137">
            <span className="text-xl leading-none" data-id="element-138">⚡</span>
            <span className="font-light" data-id="element-139">
              Acceso inmediato después de la compra
            </span>
          </div>
          <div className="flex items-center gap-3" data-id="element-140">
            <span className="text-xl leading-none" data-id="element-141">🔒</span>
            <span className="font-light" data-id="element-142">Pago 100% seguro</span>
          </div>
          <div className="flex items-center gap-3" data-id="element-143">
            <span className="text-xl leading-none" data-id="element-144">👥</span>
            <span className="font-light" data-id="element-145">
              Se puede jugar en pareja o en grupos de amigos
            </span>
          </div>
        </div>

        <CTAButton className="w-full sm:w-auto px-12 py-5 text-lg mb-8" data-id="element-146">
          QUIERO ACCEDER AHORA
        </CTAButton>
      </FadeIn>
    </div>
  </section>;
const Footer = () => <footer className="py-8 bg-black border-t border-white/5 text-center text-zinc-600 text-sm font-light" data-id="element-147">
    <p data-id="element-148">
      © {new Date().getFullYear()} El Juego del Deseo. Todos los derechos
      reservados.
    </p>
  </footer>;
export function App() {
  useEffect(() => {
    const f = window;
    const b = document;
    const e = 'script';
    const v = 'https://connect.facebook.net/en_US/fbevents.js';
    if (f.fbq) return;
    const n = f.fbq = function () {
      n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
    };
    if (!f._fbq) f._fbq = n;
    n.push = n;
    n.loaded = !0;
    n.version = '2.0';
    n.queue = [];
    const t = b.createElement(e) as HTMLScriptElement;
    t.async = !0;
    t.src = v;
    const s = b.getElementsByTagName(e)[0];
    if (s && s.parentNode) {
      s.parentNode.insertBefore(t, s);
    } else {
      b.head.appendChild(t);
    }
    f.fbq('init', '2418833905303854');
    f.fbq('track', 'PageView');
  }, []);
  return <div className="min-h-screen bg-black text-white selection:bg-[#FF2D7A]/30" data-id="element-149">
      <HeroSection data-id="element-150" />
      <IncludesSection data-id="element-151" />
      <ValueStackSection data-id="element-152" />
      <WhyItWorksSection data-id="element-153" />
      <WhoIsItForSection data-id="element-154" />
      <SocialProofSection data-id="element-155" />
      <FAQSection data-id="element-156" />
      <TrustAndPricingSection data-id="element-157" />
      <Footer data-id="element-158" />
      <noscript data-id="element-159">
        <img height="1" width="1" style={{
        display: 'none'
      }} src="https://www.facebook.com/tr?id=2418833905303854&ev=PageView&noscript=1" alt="" data-id="element-160" />
      </noscript>
    </div>;
}